/* eslint-disable no-underscore-dangle, import/prefer-default-export */
// Panel-level GA4 page-view emitter.
//
// Pushes one `virtual_page_view` event to `window.dataLayer` per "panel boundary":
// Homepage, All Cities, Country/State/County map levels, Property Detail,
// Search Results (Results/Recent/Saved), Filters, Layers, My Properties, Notes.
//
// Map pan/zoom no longer writes URL hash fragments. The multi-write cascade from a
// single user click (which can write the URL multiple times via setQueryParam +
// breadcrumbs) is coalesced into a single event via:
//   - dedupe by panel signature (panel_type + folio for property detail)
//   - "wait for title to settle" timing (see below)
//
// Title timing. The Property Detail panel sets document.title only after its
// metatag API call resolves (via `setMetaTags` in helpers/common.js, the only
// writer of document.title in the app). On a property deep load there are TWO
// title writes - the route-level metatag at ~200 ms, then the property metatag
// 5-7 s later when refreshPropertyMetatags() resolves. So we can't just settle
// on the first mutation. The tracker handles this with three coordinated signals:
//   1. <title> MutationObserver: every mutation extends the settle window by
//      SETTLE_AFTER_TITLE_MUTATION_MS. Bridges short gaps between writes.
//   2. notifyPanelReady() (exported, called from refreshPropertyMetatags after
//      setPageData lands): explicit "the final title is about to arrive" signal.
//      Schedules the fire SETTLE_AFTER_PANEL_READY_MS later so the title mutation
//      from withPageData's setMetaTags() can land first.
//   3. Hard cap: MAX_WAIT_*_MS from the URL change forces a fire even if no
//      signal ever arrives. Property Detail has a longer cap since its metatag
//      API can take several seconds.
//
// In GTM, configure ONE tag to listen for this event:
//   Trigger: Custom Event, event name = "virtual_page_view"
//   Tag:     "Google Analytics: GA4 Event", event_name = "page_view"
//   Params:  page_path, page_title, page_location, panel_type, event_id
// And set the existing "GA4 - Config" tag's "Send a page view event when this
// configuration loads" to OFF, so the only source of GA4 page_views is this event.
//
// See react/src/docs/GA4_INVESTIGATION.md for full background.

import {getQueryParam, parsePropertyAlias} from '../helpers/common';
import {sitemapLink} from '../Components/Containers/Sitemap/constValues';
import {browseLink} from '../Components/Containers/Browse/constValues';

const MIN_WAIT_AFTER_URL_CHANGE_MS = 300;
const SETTLE_AFTER_TITLE_MUTATION_MS = 400;
// notifyPanelReady() means the caller's API resolved and just dispatched the
// pageData/metatag update. Give withPageData -> setMetaTags() a moment to
// propagate to document.title before we read it.
const SETTLE_AFTER_PANEL_READY_MS = 200;
// Hard caps. Property Detail's metatag GraphQL call can take 5-8s in the wild,
// so it gets a longer ceiling.
const MAX_WAIT_DEFAULT_MS = 3000;
const MAX_WAIT_PROPERTY_DETAIL_MS = 10000;
const PROPERTY_SHORT_PREFIX = '/zoning-detail/';

const WRAP_FLAG = '__pageViewTrackerWrapped';

// Boot title from public/index.html. While document.title is one of these, we
// haven't received a real setMetaTags() write yet and should keep waiting.
const KNOWN_PLACEHOLDER_TITLES = new Set(['React App', '']);

const PANEL = {
  homepage: 'homepage',
  allCities: 'all_cities',
  browseDirectory: 'browse_directory',
  countryMap: 'country_map',
  stateMap: 'state_map',
  countyMap: 'county_map',
  myProperties: 'my_properties',
  notes: 'notes',
  propertyDetail: 'property_detail',
  filters: 'filters',
  layers: 'layers',
  searchResults: 'search_results',
  map: 'map',
};

const GEO_PATH_PREFIX = {
  [PANEL.countryMap]: '/country/',
  [PANEL.stateMap]: '/state/',
  [PANEL.countyMap]: '/county/',
};

// Shared on window so duplicate module instances (e.g. HMR or split chunks) cannot
// each maintain their own lastSignature and push three identical page_view events.
function trackerState() {
  if (!window.__pageViewTracker) {
    window.__pageViewTracker = {
      lastSignature: null,
      lastPanelUrlKey: null,
      lastPanelSearch: null,
      pending: null,
    };
  }
  return window.__pageViewTracker;
}

function normalizeOverlayValue(value) {
  if (value == null) return '';
  return String(value).toLowerCase();
}

function normalizePanelSearch(search) {
  const params = new URLSearchParams(search || '');
  const leftOverlay = normalizeOverlayValue(params.get('leftOverlay'));
  const activeTab = normalizeOverlayValue(params.get('activeTab'));
  const shouldTrackPropertyParams = !leftOverlay || leftOverlay === 'properties';
  const folio = shouldTrackPropertyParams ? String(params.get('folio') || '') : '';
  const propertyId = shouldTrackPropertyParams ? String(params.get('property_id') || '') : '';

  return {
    leftOverlay,
    activeTab,
    folio,
    propertyId,
  };
}

// Panel boundary key. Do not include filter-encoded query params here.
function getPanelBoundaryUrlKey() {
  const {pathname, search} = window.location;
  const normalized = normalizePanelSearch(search);
  return [
    `path=${pathname}`,
    `overlay=${normalized.leftOverlay}`,
    `tab=${normalized.activeTab}`,
    `folio=${normalized.folio}`,
    `property=${normalized.propertyId}`,
  ].join('|');
}

// Filter changes rewrite search params (e.g. _tags_overlay, range keys) but must not
// count as panel navigation. Compare only panel-defining search params.
function isFilterOnlySearchDelta(prevSearch, nextSearch) {
  if (prevSearch == null || nextSearch == null || prevSearch === nextSearch) {
    return false;
  }
  const prevNormalized = normalizePanelSearch(prevSearch);
  const nextNormalized = normalizePanelSearch(nextSearch);
  return (
    prevNormalized.leftOverlay === nextNormalized.leftOverlay
    && prevNormalized.activeTab === nextNormalized.activeTab
    && prevNormalized.folio === nextNormalized.folio
    && prevNormalized.propertyId === nextNormalized.propertyId
  );
}

function notePanelUrlChange() {
  const state = trackerState();
  const key = getPanelBoundaryUrlKey();
  const search = window.location.search || '';
  const prevKey = state.lastPanelUrlKey;
  const prevSearch = state.lastPanelSearch;
  state.lastPanelUrlKey = key;
  state.lastPanelSearch = search;
  if (prevKey == null) return true;
  if (prevKey === key) return false;
  if (isFilterOnlySearchDelta(prevSearch, search)) return false;
  return true;
}

function classifyGeoPanel(pathname) {
  if (pathname.indexOf(GEO_PATH_PREFIX[PANEL.countryMap]) === 0) {
    return {panel_type: PANEL.countryMap, geo_path: pathname};
  }
  if (pathname.indexOf(GEO_PATH_PREFIX[PANEL.stateMap]) === 0) {
    return {panel_type: PANEL.stateMap, geo_path: pathname};
  }
  if (pathname.indexOf(GEO_PATH_PREFIX[PANEL.countyMap]) === 0) {
    return {panel_type: PANEL.countyMap, geo_path: pathname};
  }
  return null;
}

function resolvePropertyFolio(pathname, queryUrl) {
  const folioParam = getQueryParam('folio', queryUrl);
  if (folioParam) {
    return String(folioParam);
  }
  if (pathname.indexOf(PROPERTY_SHORT_PREFIX) === 0) {
    const alias = pathname.slice(PROPERTY_SHORT_PREFIX.length);
    const parsed = parsePropertyAlias(alias);
    if (parsed?.folio) {
      return String(parsed.folio);
    }
  }
  return '';
}

function classifyPanel() {
  const {pathname, search} = window.location;
  const queryUrl = `${pathname}${search}`;
  const rawOverlay = getQueryParam('leftOverlay', queryUrl);
  const rawTab = getQueryParam('activeTab', queryUrl);
  const leftOverlay = rawOverlay == null ? '' : String(rawOverlay).toLowerCase();
  const activeTab = rawTab == null ? '' : String(rawTab).toLowerCase();
  const propertyFolio = resolvePropertyFolio(pathname, queryUrl);

  // Top-level routes (pathname is enough).
  if (pathname === '/') {
    return {panel_type: PANEL.homepage};
  }
  if (pathname === sitemapLink) {
    return {panel_type: PANEL.allCities};
  }
  if (pathname === browseLink || pathname.indexOf(`${browseLink}/`) === 0) {
    return {panel_type: PANEL.browseDirectory};
  }
  if (pathname.indexOf('/crm/properties') === 0) {
    return {panel_type: PANEL.myProperties};
  }
  if (pathname.indexOf('/crm/notes') === 0) {
    return {panel_type: PANEL.notes};
  }

  switch (leftOverlay) {
    case 'filters':
      return {panel_type: PANEL.filters};
    case 'layers':
      return {panel_type: PANEL.layers};
    case 'results': {
      const tab = activeTab || 'results';
      return {panel_type: `${PANEL.searchResults}_${tab}`};
    }
    default:
      break;
  }

  const isPropertyShortPath = pathname.indexOf(PROPERTY_SHORT_PREFIX) === 0;
  if (isPropertyShortPath || leftOverlay === 'properties') {
    return {panel_type: PANEL.propertyDetail, folio: propertyFolio};
  }
  // Map click: folio is written before leftOverlay=properties.
  if (propertyFolio) {
    return {panel_type: PANEL.propertyDetail, folio: propertyFolio};
  }

  const geoPanel = classifyGeoPanel(pathname);
  if (geoPanel) {
    return geoPanel;
  }
  return {panel_type: PANEL.map};
}

function panelSignature(panel) {
  const key = panel.folio || panel.geo_path || '';
  return `${panel.panel_type}|${key}`;
}

function buildEventId(panel, pagePath) {
  const dedupeKey = panel.folio || panel.geo_path || '';
  const keyPart = dedupeKey ? `_${dedupeKey.replace(/\//g, '_')}` : '';
  return `pv_${panel.panel_type}${keyPart}_${pagePath}`;
}

function buildPayload(panel) {
  const path = `${window.location.pathname}${window.location.search}`;
  return {
    event: 'virtual_page_view',
    panel_type: panel.panel_type,
    page_path: path,
    page_title: document.title || '',
    // Match page_path (no hash) — Mapbox camera hash must not differentiate locations.
    page_location: `${window.location.origin}${path}`,
    // Lets GA4 collapse accidental duplicate sends for the same panel boundary.
    event_id: buildEventId(panel, path),
  };
}

function fireNow() {
  const state = trackerState();
  const p = state.pending;
  state.pending = null;
  if (!p) return;
  const panel = classifyPanel();
  const sig = panelSignature(panel);
  if (sig === state.lastSignature) return;
  state.lastSignature = sig;
  if (!Array.isArray(window.dataLayer)) {
    window.dataLayer = [];
  }
  window.dataLayer.push(buildPayload(panel));
}

function waitsForPropertyPanelReady(panel) {
  return panel.panel_type === PANEL.propertyDetail;
}

function waitsForGeoMetatags(panel) {
  return (
    panel.panel_type === PANEL.countryMap || panel.panel_type === PANEL.stateMap || panel.panel_type === PANEL.countyMap
  );
}

function waitsForTitleSettle(panel) {
  if (KNOWN_PLACEHOLDER_TITLES.has(document.title || '')) return true;
  const state = trackerState();
  const wasProperty = state.lastSignature?.startsWith(`${PANEL.propertyDetail}|`);
  return wasProperty && panel.panel_type !== PANEL.propertyDetail;
}

function shouldWaitForTitle(panel) {
  return waitsForPropertyPanelReady(panel) || waitsForGeoMetatags(panel) || waitsForTitleSettle(panel);
}

function maxWaitFor(panel) {
  return panel.panel_type === PANEL.propertyDetail ? MAX_WAIT_PROPERTY_DETAIL_MS : MAX_WAIT_DEFAULT_MS;
}

function armPendingTimer() {
  const state = trackerState();
  const {pending} = state;
  if (!pending) return;
  if (pending.timer) clearTimeout(pending.timer);
  const now = Date.now();
  const panel = classifyPanel();
  const minAt = pending.firstUrlChangeAt + MIN_WAIT_AFTER_URL_CHANGE_MS;
  const maxAt = pending.firstUrlChangeAt + maxWaitFor(panel);

  let fireAt;
  if (pending.panelReadyAt) {
    // Explicit "panel's API resolved" signal from the panel itself. Fire shortly
    // after so the resulting setMetaTags() write propagates to document.title.
    fireAt = pending.panelReadyAt + SETTLE_AFTER_PANEL_READY_MS;
  }
  else if (shouldWaitForTitle(panel)) {
    if (waitsForPropertyPanelReady(panel)) {
      // Property detail: wait for notifyPanelReady() or hard cap.
      fireAt = maxAt;
    }
    else if (waitsForGeoMetatags(panel)) {
      if (pending.panelReadyAt) {
        fireAt = pending.panelReadyAt + SETTLE_AFTER_PANEL_READY_MS;
      }
      else if (pending.lastTitleMutationAt) {
        fireAt = pending.lastTitleMutationAt + SETTLE_AFTER_TITLE_MUTATION_MS;
      }
      else {
        fireAt = maxAt;
      }
    }
    else if (pending.panelReadyAt) {
      // Leaving property (e.g. Searched Properties) after market metatags resolve.
      fireAt = pending.panelReadyAt + SETTLE_AFTER_PANEL_READY_MS;
    }
    else if (pending.lastTitleMutationAt) {
      fireAt = pending.lastTitleMutationAt + SETTLE_AFTER_TITLE_MUTATION_MS;
    }
    else {
      fireAt = maxAt;
    }
  }
  else if (pending.lastTitleMutationAt) {
    // Got a title mutation -> fire SETTLE ms after it (so we capture the
    // *final* title from a burst of mutations).
    fireAt = pending.lastTitleMutationAt + SETTLE_AFTER_TITLE_MUTATION_MS;
  }
  else {
    // Title isn't expected to change for this panel - fire at min wait.
    fireAt = minAt;
  }
  fireAt = Math.min(maxAt, Math.max(minAt, fireAt));
  pending.timer = setTimeout(fireNow, Math.max(0, fireAt - now));
}

function scheduleFire() {
  const state = trackerState();
  // Skip non-panel URL writes and the multi-write cascade: if the panel signature
  // hasn't changed since the last fired event, there's nothing to track.
  const sig = panelSignature(classifyPanel());
  if (sig === state.lastSignature) return;

  if (!state.pending) {
    state.pending = {
      firstUrlChangeAt: Date.now(),
      lastTitleMutationAt: null,
      panelReadyAt: null,
      timer: null,
    };
  }
  armPendingTimer();
}

function onTitleMutation() {
  const state = trackerState();
  if (!state.pending) return;
  state.pending.lastTitleMutationAt = Date.now();
  armPendingTimer();
}

// Called from a panel's data-loading code (e.g. withLoadPropertyController's
// refreshPropertyMetatags) right after the setPageData()/setMetaTags() chain
// that produces the final document.title. Tells the tracker the panel is ready;
// the actual fire happens SETTLE_AFTER_PANEL_READY_MS later so that the
// resulting <title> mutation has time to propagate.
export function notifyPanelReady() {
  const state = trackerState();
  if (!state.pending) return;
  state.pending.panelReadyAt = Date.now();
  armPendingTimer();
}

// Called when the property detail panel finishes opening (map click, results row,
// deep link). Covers cases where Redux opens showProperties before the URL has
// leftOverlay=properties but folio is already in the query or short path.
export function schedulePropertyPanelOpen() {
  scheduleFire();
}

function installTitleObserver() {
  if (window.__pageViewTitleObserver) return;
  const titleEl = typeof document !== 'undefined' ? document.querySelector('title') : null;
  if (!titleEl || typeof MutationObserver === 'undefined') return;
  const observer = new MutationObserver(onTitleMutation);
  observer.observe(titleEl, {childList: true, characterData: true, subtree: true});
  window.__pageViewTitleObserver = observer;
}

function onHistoryWrite() {
  if (notePanelUrlChange()) {
    scheduleFire();
  }
}

function wrapHistoryMethod(methodName) {
  const orig = window.history[methodName];
  if (typeof orig !== 'function' || orig[WRAP_FLAG]) return;
  const wrapped = function patched(...args) {
    const ret = orig.apply(this, args);
    onHistoryWrite();
    return ret;
  };
  wrapped[WRAP_FLAG] = true;
  window.history[methodName] = wrapped;
}

export function installPageViewTracker() {
  if (typeof window === 'undefined' || window.__pageViewTrackerInstalled) return;
  window.__pageViewTrackerInstalled = true;

  // All SPA URL changes go through window.history.pushState/replaceState
  // (react-router and setQueryParam). classifyPanel() only reads pathname and
  // panel-defining query params.
  wrapHistoryMethod('pushState');
  wrapHistoryMethod('replaceState');

  // Back/forward: apply the same panel-boundary filtering.
  window.addEventListener('popstate', onHistoryWrite);

  // <title> mutations - resets the settle timer so a late setMetaTags()
  // write (after the property metatag API call resolves) gets captured in
  // the page_title we send to GA4.
  installTitleObserver();

  // Initial page view after first paint so any synchronous title set during boot
  // has happened. Do not also subscribe to the Redux store history: App uses a
  // separate BrowserRouter history, and history.listen fires immediately on
  // subscribe which duplicated the initial kick and produced triple page_views.
  const kick = () => scheduleFire();
  if (document.readyState === 'complete') {
    setTimeout(kick, 0);
  }
  else {
    window.addEventListener('load', kick, {once: true});
  }
}

export const __test__ = {
  getPanelBoundaryUrlKey,
  isFilterOnlySearchDelta,
};
